import { FC } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartCard from './ChartCard';
import { useThemeColors } from './useThemeColors';

interface LineChartCardProps {
  title: string;
  subtitle?: string;
  data: Array<Record<string, string | number>>;
  dataKey: string;
  xKey?: string;
  yLabel?: string;
  height?: number;
}

const LineChartCard: FC<LineChartCardProps> = ({
  title,
  subtitle,
  data,
  dataKey,
  xKey = 'date',
  yLabel,
  height,
}) => {
  const colors = useThemeColors();

  if (!data || data.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} height={height}>
        <div className="w-full h-full flex items-center justify-center text-content-muted text-sm">
          Sem dados no período
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} subtitle={subtitle} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" />
          <XAxis
            dataKey={xKey}
            stroke={colors.textMuted}
            tick={{ fill: colors.textMuted, fontSize: 13 }}
            tickFormatter={formatXTick}
          />
          <YAxis
            stroke={colors.textMuted}
            tick={{ fill: colors.textMuted, fontSize: 13 }}
            label={
              yLabel
                ? {
                    value: yLabel,
                    angle: -90,
                    position: 'insideLeft',
                    fill: colors.textMuted,
                    fontSize: 13,
                  }
                : undefined
            }
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              color: colors.text,
              fontSize: 14,
            }}
            labelStyle={{ color: colors.textMuted }}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={colors.accent}
            strokeWidth={2}
            dot={{ fill: colors.accent, r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

function formatXTick(value: string | number): string {
  if (typeof value !== 'string') return String(value);
  // YYYY-MM-DD → DD/MM
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return `${m[3]}/${m[2]}`;
  // YYYY-MM → MM/YY
  const mo = /^(\d{4})-(\d{2})$/.exec(value);
  if (mo) return `${mo[2]}/${mo[1].slice(2)}`;
  return value;
}

export default LineChartCard;
